import { useCallback, useMemo } from 'react';
import { keepPreviousData, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  fetchSceneFrameMentions,
  sceneFrameMentionsQueryKey,
  type SceneFrameMentionResponse,
  type SceneFrameMentionsResponse,
} from '../../../../features/scenarios/api';
import type { SceneFrameMentionRecord } from '../../state/mentionUtils';

// 署名付きURLの有効期限 (1時間)より短く保つ
const STALE_TIME_MS = 30 * 60 * 1000;
const EMPTY_THUMBNAILS: Record<string, string> = {};
const EMPTY_MISSING: string[] = [];

export type SceneFrameMentionThumbnails = {
  /** フレーム素材ID → フレーム素材自身の表示用URL。 */
  thumbnails: Record<string, string>;
  /** フレーム素材が無い・削除済みと分かったID (チップは「参照先なし」)。 */
  missingFrameIds: string[];
  /** 挿入API (POST .../frame-mention)の成功結果をキャッシュへ書き、再取得を待たずに表示する。 */
  applyInsertResult: (result: SceneFrameMentionResponse) => void;
};

/**
 * 他シーンの動画フレームの@メンション記録について、チップ表示用のサムネイルと
 * 「参照先なし」をGET .../frame-mentionsから得る
 * (docs/features/scene-video-frame-materials-visibility-plan.md §2.2/§2.3)。
 *
 * シナリオ単位のReact Queryキャッシュ (キーに記録のID集合を含む)なので、シーンごとの
 * SceneDetailColumnが同時に呼んでも取得は1回。記録が空なら呼ばない。
 */
export function useSceneFrameMentionThumbnails(
  scenarioId: string | null | undefined,
  records: Record<string, SceneFrameMentionRecord>,
): SceneFrameMentionThumbnails {
  const queryClient = useQueryClient();
  const frameIds = useMemo(() => Object.keys(records), [records]);
  const queryKey = sceneFrameMentionsQueryKey(scenarioId ?? '', frameIds);
  const query = useQuery({
    queryKey,
    queryFn: () => fetchSceneFrameMentions(scenarioId as string),
    enabled: !!scenarioId && frameIds.length > 0,
    staleTime: STALE_TIME_MS,
    // 記録の増減でキーが変わっても、取り直す間は前回のサムネイルを出し続ける
    placeholderData: keepPreviousData,
  });

  const entries = frameIds.length > 0 ? query.data?.entries : undefined;
  const thumbnails = useMemo(() => {
    if (!entries) return EMPTY_THUMBNAILS;
    const map: Record<string, string> = {};
    for (const [id, entry] of Object.entries(entries)) {
      if (entry.thumbnail_url && !entry.missing) map[id] = entry.thumbnail_url;
    }
    return map;
  }, [entries]);
  const missingFrameIds = useMemo(() => {
    if (!entries) return EMPTY_MISSING;
    return Object.entries(entries).filter(([, entry]) => entry.missing).map(([id]) => id);
  }, [entries]);

  const applyInsertResult = useCallback((result: SceneFrameMentionResponse) => {
    if (!scenarioId) return;
    // 挿入前のキャッシュ (どのID集合のキーでも)を引き継ぎ、挿入したフレームを足して新しいキーへ書く
    const previous = queryClient.getQueriesData<SceneFrameMentionsResponse>({
      queryKey: ['scene-frame-mentions', scenarioId],
    }).reduce<SceneFrameMentionsResponse['entries']>(
      (acc, [, data]) => ({ ...acc, ...(data?.entries ?? {}) }),
      {},
    );
    const nextIds = Object.keys(result.scene_frame_mentions || {});
    const nextEntries: SceneFrameMentionsResponse['entries'] = {};
    for (const id of nextIds) {
      if (previous[id]) nextEntries[id] = previous[id];
    }
    nextEntries[result.frame_material_id] = {
      ...result.entry,
      thumbnail_url: result.thumbnail_url,
      missing: false,
    };
    const nextKey = sceneFrameMentionsQueryKey(scenarioId, nextIds);
    queryClient.setQueryData<SceneFrameMentionsResponse>(nextKey, { entries: nextEntries });
    // 別タブの挿入などで手元に無い記録が混じっていれば、表示は保ったまま裏で取り直す
    if (nextIds.some((id) => !nextEntries[id])) {
      void queryClient.invalidateQueries({ queryKey: nextKey });
    }
  }, [queryClient, scenarioId]);

  return { thumbnails, missingFrameIds, applyInsertResult };
}
